/**
 * CSV for one bulk conversion (one row per file).
 * Columns: test metadata + file row (no legacy wide Pear export).
 */

import {
  isConversionBusy,
  isConversionFailed,
  isSuccessfulConversionFile,
} from './bulkConversionStatus'

/** Count successful vs total files for the post-upload status bar summary. */
export function getBulkConversionFileSummary(conversion) {
  const files = conversion?.files ?? []
  const total =
    conversion?.total_files > 0 ? conversion.total_files : files.length

  if (files.length === 0) {
    const allOk =
      conversion?.status &&
      !isConversionBusy(conversion) &&
      !isConversionFailed(conversion)
    return {
      successCount: allOk && total > 0 ? total : 0,
      totalCount: total,
    }
  }

  const successCount = files.filter(isSuccessfulConversionFile).length

  return {
    successCount,
    totalCount: total > 0 ? total : files.length,
  }
}

function escapeCsvCell(value) {
  const s = value == null ? '' : String(value)
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

const HEADER = [
  'Test Id',
  'Test name',
  'Content URL',
  'Status (draft)',
  'Conversion Status',
  'FileId',
  'File Name',
  'Created At',
]

function indexTestsById(tests) {
  const map = new Map()
  for (const t of tests || []) {
    if (!t || t._id == null || t._id === '') continue
    map.set(String(t._id).trim(), t)
  }
  return map
}

/** Edulastic test `status` for CSV (same field as test list). */
function testStatusForCsv(test) {
  if (!test || test.status == null || test.status === '') return ''
  return String(test.status)
}

export function buildBulkConversionCsv(conversion, tests = []) {
  if (!conversion?.conversion_id) {
    const line = (cells) => cells.map(escapeCsvCell).join(',')
    return `\uFEFF${line(HEADER)}\r\n${line(Array(HEADER.length).fill(''))}`
  }

  const rows = [HEADER]
  const files = conversion.files ?? []
  const testById = indexTestsById(tests)

  const fileRow = (f) => {
    const cid =
      f.content_id != null && String(f.content_id).trim() !== ''
        ? String(f.content_id).trim()
        : ''
    const test = cid ? testById.get(cid) : null
    const testName = test ? String(test.title ?? test.name ?? '') : ''
    const statusDraft = test ? testStatusForCsv(test) : ''
    return [
      f.content_id ?? '',
      testName,
      f.content_url ?? '',
      statusDraft,
      f.status ?? '',
      f.file_id ?? '',
      f.name ?? '',
      f.created_at ?? '',
    ]
  }

  if (files.length === 0) {
    rows.push([
      '',
      '',
      '',
      '',
      conversion.status ?? '',
      '',
      '',
      conversion.created_at ?? '',
    ])
  } else {
    files.forEach((f) => rows.push(fileRow(f)))
  }

  const line = (cells) => cells.map(escapeCsvCell).join(',')
  return `\uFEFF${rows.map(line).join('\r\n')}`
}

export function downloadCsvFile(filename, csvBody) {
  const blob = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
